/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';
import {
    SETTINGS_VIDEO_WINDOW_SET_ACTIVE,
    SETTINGS_VIDEO_WINDOW_SET_SELECTED_OPTION,
    SETTINGS_VIDEO_WINDOW_SET_GHOST_MODE,
} from './settingsvideowindow.types';
import {
    settingsVideoWindowSetActiveAction,
    settingsVideoWindowEnableGhostModeAction,
    settingsVideoWindowSetSelectedOptionAction,
} from './settingsvideowindow.actions';
import { setScreenTaskController, showSettingsScreen } from '../../screens/screens.reducers';

import { InteractiveComponents } from '../../../core/enums/InteractiveComponents';
import {
    clearActiveInteractiveComponent,
    setActiveInteractiveComponent,
} from '../../interactivelayer/interactiveLayer.reducers';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { VideoManager } from '../../../core/videoplayer';
import { DeviceModel } from '../../../core/device';
import { Log, QoEAppLogger, VuLogger } from '../../../core/logging';
import { ServicesClientImpl } from '../../../core/impls';

const TAG = 'SettingsVideoWindow';
const settingsVideoWindowButtonId = 'settings-video-window-button';

const initialState: {
    isActive: boolean;
    isGhostModeActive: boolean;
    selectedOption: number;
} = {
    isActive: false,
    isGhostModeActive: false,
    selectedOption: 0,
};

const settingsVideoWindowReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case SETTINGS_VIDEO_WINDOW_SET_ACTIVE:
            return {
                ...state,
                isActive: payload.isActive,
            };
        case SETTINGS_VIDEO_WINDOW_SET_SELECTED_OPTION:
            return {
                ...state,
                selectedOption: payload.selectedOption,
            };
        case SETTINGS_VIDEO_WINDOW_SET_GHOST_MODE:
            return {
                ...state,
                isGhostModeActive: payload.isGhostModeActive,
            };
        default:
            return state;
    }
};

export const onKeyBackSettingsVideoWindowOperations = () => (dispatch: Function) => {
    dispatch(showSettingsScreen());
    dispatch(settingsVideoWindowHide());
};

export const onKeyLeftSettingsVideoWindowOperations =
    () => (dispatch: Function, getState: Function) => {
        const { selectedOption } = getState().settingsVideoWindow;
        if (selectedOption === 0) dispatch(settingsVideoWindowSetSelectedOptionAction(1));
        else dispatch(settingsVideoWindowSetSelectedOptionAction(0));
    };

export const onKeyOkSettingsVideoWindowOperations =
    () => async (dispatch: Function, getState: Function) => {
        // First argument should be the dispatch function.
        const { selectedOption } = getState().settingsVideoWindow;
        DeviceModel.getAppSettings().setIsVideoWindowEnabled(!selectedOption);
        try {
            const services = new ServicesClientImpl();

            if (DeviceModel.getAppSettings().isVideoWindowEnabled()) {
                const playable = await services.getLastPlayedOrFallbackToFirstByChannelNumber(
                    DeviceModel.getAppSettings().getLastTunedChannelNumber(),
                );
                VideoManager.getInstance().mount();
                VideoManager.getInstance().startPlayback(playable);
            } else {
                QoEAppLogger.setWatchContentEndReason(
                    QoEAppLogger.PlaybackEndReason.USER_CLOSED_THE_VIDEO_WINDOW,
                );
                QoEAppLogger.logPlaybackStopped();
                VideoManager.getInstance().unmount();
                VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.VIDEO_WINDOW_OFF);
            }
        } catch (blunder) {
            Log.d(TAG, 'handleOnVideoManagerReady ', blunder);
        }
        dispatch(settingsVideoWindowHide());
        dispatch(showSettingsScreen());
    };

export const onKeyRightSettingsVideoWindowOperations = () => (dispatch: Function) => {
    dispatch(onKeyLeftSettingsVideoWindowOperations());
};

export const onMouseClickInteractiveLayerOperations = () => (dispatch: Function) => {
    dispatch(onKeyLeftSettingsVideoWindowOperations());
};

export const onMouseOutSettingsVideoWindowOperations =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;
        const { previousScreenTaskController } = getState().screens;
        dispatch(settingsVideoWindowEnableGhostMode());
        if (value === InteractiveComponents.EAST || value === InteractiveComponents.WEST) {
            dispatch(setScreenTaskController(previousScreenTaskController));
            dispatch(clearActiveInteractiveComponent());
        }
    };

export const onMouseOverSettingsVideoWindowOperations =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;
        if (value === InteractiveComponents.EAST || value === InteractiveComponents.WEST) {
            dispatch(setActiveInteractiveComponent(value));
            dispatch(
                setScreenTaskController(ScreenTaskControllerTypes.SETTINGS_INTERACTIVE_LAYER_TASKS),
            );
        } else if (value === settingsVideoWindowButtonId) {
            dispatch(settingsVideoWindowDisableGhostMode());
        } else {
            dispatch(settingsVideoWindowEnableGhostMode());
        }
    };

export const settingsVideoWindowDisableGhostMode = () => (dispatch: Function) => {
    dispatch(settingsVideoWindowEnableGhostModeAction(false));
};

export const settingsVideoWindowEnableGhostMode = () => (dispatch: Function) => {
    dispatch(settingsVideoWindowEnableGhostModeAction(true));
};

export const settingsVideoWindowHide = () => (dispatch: Function) => {
    dispatch(settingsVideoWindowSetActiveAction(false));
};

export const settingsVideoWindowShow = () => (dispatch: Function) => {
    dispatch(settingsVideoWindowSetActiveAction(true));
};

export default settingsVideoWindowReducer;
